import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Loader, TitleDivider, useTheme } from '@Sage/hoi-poi-ui';

import { getLiteral } from 'utils/getLiteral';

import EngagementFunnel from './EngagementFunnel';
import LinkPerformance from './LinkPerformance';
import PerformanceOverTime from './PerformanceOverTime';
import ScoreCard from './ScoreCard';
import { useCampaignSummary } from './useCampaignSummary';

const CampaignSummary = memo(({ campaign: campaignFromDetail, campaignId, isActive }) => {
    const theme = useTheme();
    const {
        isLoading,
        campaign: campaignFromEventsDetail,
        engagementScorecards,
        engagementFunnel,
        deliveryHealth,
        linkPerformance,
    } = useCampaignSummary({ campaignId, isActive });

    const campaignForChart = useMemo(() => {
        if (campaignFromDetail && campaignFromEventsDetail) {
            return { ...campaignFromEventsDetail, ...campaignFromDetail };
        }
        return campaignFromDetail ?? campaignFromEventsDetail ?? null;
    }, [campaignFromDetail, campaignFromEventsDetail]);

    const scorecards = useMemo(() => {
        if (!engagementScorecards) {
            return [];
        }
        return [
            {
                key: 'opens',
                value: engagementScorecards.uniqueOpens,
                label: getLiteral('label_marketing_campaigns_metrics_unique_opens'),
                sublabel: `${getLiteral('label_marketing_campaigns_metrics_total_opens')}: ${engagementScorecards.totalOpens}`,
                valueColor: theme.colors.semantic.positive400,
            },
            {
                key: 'clicks',
                value: engagementScorecards.uniqueClicks,
                label: getLiteral('label_marketing_campaigns_metrics_unique_clicks'),
                sublabel: `${getLiteral('label_marketing_campaigns_metrics_total_clicks')}: ${engagementScorecards.totalClicks}`,
                valueColor: theme.colors.semantic.info400,
            },
            {
                key: 'unsubscribes',
                value: engagementScorecards.canceledSubscriptions,
                label: getLiteral('label_marketing_campaigns_metrics_canceled_subscriptions'),
                sublabel: getLiteral('label_marketing_campaigns_metrics_unsubscribed_from_list'),
                valueColor: theme.colors.semantic.negative450,
            },
        ];
    }, [engagementScorecards, theme.colors.semantic]);

    if (!isActive) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="fm-campaign-summary__loading">
                <Loader size="big" />
            </div>
        );
    }

    return (
        <div className="fm-campaign-summary">
            <TitleDivider type="subtitle">
                {getLiteral('label_marketing_campaigns_metrics_engagement')}
            </TitleDivider>
            <div className="fm-campaign-summary__scorecards">
                {scorecards.map((card) => (
                    <ScoreCard
                        key={card.key}
                        value={card.value}
                        label={card.label}
                        sublabel={card.sublabel}
                        valueColor={card.valueColor}
                    />
                ))}
            </div>
            <EngagementFunnel funnel={engagementFunnel} health={deliveryHealth} />
            <PerformanceOverTime
                campaign={campaignForChart}
                campaignId={campaignId}
                isActive={isActive}
            />
            <LinkPerformance links={linkPerformance} />
        </div>
    );
});

CampaignSummary.displayName = 'CampaignSummary';

CampaignSummary.propTypes = {
    campaign: PropTypes.object,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isActive: PropTypes.bool,
};

export default CampaignSummary;
